<form
  class="product-wrapper"
  [formGroup]="productData"
  (ngSubmit)="onSubmit()"
  fxLayout="column"
>
  <div mat-dialog-title fxLayout="row" fxLayoutAlign="space-between center">
    <h2 fxFlex="50">
      {{ product.id ? 'Edit' : 'Update' }}
      {{ isMedicalType ? 'Procedure' : 'Product' }}
    </h2>
    <div fxFlex fxLayout="row" fxLayoutAlign="space-between center">
      <h2>{{ isMedicalType ? 'Procedure' : 'Product' }} Prices</h2>
      <button
        mat-stroked-button
        color="primary"
        type="button"
        (click)="productPrices.addProductPrice()"
        [disabled]="!this.product.id"
      >
        <mat-icon>add</mat-icon>
        <span>Add negotiated rate</span>
      </button>
    </div>
  </div>

  <mat-progress-bar [mode]="(isLoading$ | async) ? 'indeterminate': 'determinate'"></mat-progress-bar>

  <div *ngIf="fromProceduresCard" class="text-warn text-center p-2">
    This dialog will update the global procedure configuration.
  </div>

  <div fxLayout="column" class="product-container">
    <div fxLayout="row">
      <div class="product-data" fxFlex="50">
        <mat-dialog-content fxLayout="column" fxLayoutGap="1rem">
          <mat-form-field appearance="fill" fxFlex>
            <mat-label>Name</mat-label>
            <input matInput formControlName="name" required cdkFocusInitial />
            <mat-error *ngIf="productData.controls.name.invalid">{{
              getErrorMessage('name')
            }}</mat-error>
          </mat-form-field>

          <mat-form-field appearance="fill" fxFlex>
            <mat-label>Type</mat-label>
            <mat-select formControlName="type" required (selectionChange)="onTypeChanged($event)">
              <mat-option
                *ngFor="let productType of productTypes"
                [value]="productType.value"
              >
                {{ productType.viewValue }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="fill" fxFlex *ngIf="!isMedicalType">
            <mat-label>SKU</mat-label>
            <input matInput formControlName="sku" required />
            <mat-error *ngIf="productData.controls['sku'].invalid">{{
              getErrorMessage('sku')
            }}</mat-error>
            <mat-hint>Stock-keeping unit. Must be unique.</mat-hint>
          </mat-form-field>

          <mat-form-field *ngIf="isMedicalType" appearance="fill">
            <mat-label *ngIf="productData.value.type === 'M'">
              CPT/HCPCS Codes
            </mat-label>
            <mat-label *ngIf="productData.value.type === 'D'">
              CDT Codes
            </mat-label>
            <mat-chip-grid #chipList>
              <mat-chip-row
                *ngFor="let code of productData.value.codes"
                [removable]="true"
                (removed)="removeCode(code)">
                {{ code }}
                <mat-icon matChipRemove>cancel</mat-icon>
              </mat-chip-row>
              <input
                #codeFilterInput
                formControlName="code"
                [matAutocomplete]="autoCode"
                [matChipInputFor]="chipList"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                (matChipInputTokenEnd)="optionSelected($event.value)"
              >
            </mat-chip-grid>
            <mat-autocomplete
              autoActiveFirstOption
              #autoCode="matAutocomplete"
              (optionSelected)="optionSelected($event.option.value)"
            >
              <mat-option
                *ngFor="let cpt of filteredCptCode$ | async"
                [value]="cpt.code"
                [matTooltip]="cpt.name"
              >
                [{{ cpt.code }}] {{ cpt.name }}
              </mat-option>
            </mat-autocomplete>
            <mat-error>{{ getErrorMessage('code') }}</mat-error>
          </mat-form-field>

          <mmx-code-modifiers-form
            *ngIf="isMedicalType"
            [codes]="productData.value.codes"
            formControlName="modifiers"
          >
          </mmx-code-modifiers-form>

          <mat-form-field appearance="fill" fxFlex>
            <mat-label>{{ 'common.resources' | translate }}</mat-label>
            <mmx-resource-selector-input
              formControlName="resources"
              multiple="true"
            ></mmx-resource-selector-input>
          </mat-form-field>

          <ng-container *ngIf="productData.value.type === 'M'">
            <ng-container *ngIf="product?.recommendedServiceCodes?.length > 0">
              <strong>Recommended Service Codes:</strong>

              <ul class="mt-0">
                <ng-container *ngFor="let serviceType of getSystemServiceTypeDescriptions()">
                  <li>[{{ serviceType.code }}] {{ serviceType.name | titlecase }}</li>
                </ng-container>
              </ul>

              <mat-slide-toggle formControlName="usingRecommendServiceCodes">
                Use system recommended service codes
              </mat-slide-toggle>
            </ng-container>

            <ng-container *ngIf="!productData.value.usingRecommendServiceCodes">
              <mat-form-field
                appearance="fill"
                fxFlex
              >
                <mat-label>X12 Service Codes</mat-label>
                <mat-select formControlName="serviceCodes" multiple>
                  <mat-option>
                    <ngx-mat-select-search
                      [formControl]="serviceFilterCtrl"
                      placeholderLabel="Find service…"
                      [noEntriesFoundLabel]="'No matching service found'"
                    ></ngx-mat-select-search>
                  </mat-option>
                  <mat-option
                    *ngFor="let serviceType of filteredServiceTypes | async"
                    [value]="serviceType.code"
                  >
                    [{{ serviceType.code }}] {{ serviceType.name | titlecase }}
                  </mat-option>
                </mat-select>
                <mat-hint>
                  Customize which parts of health plans are pulled.
                </mat-hint>
                <!-- <mat-error *ngIf="productData.controls['code'].invalid">{{
                  getErrorMessage('serviceCodes')
                }}</mat-error> -->
              </mat-form-field>

              <mat-slide-toggle formControlName="updateOtherProducts">
                Update other procedures with this CPT code
              </mat-slide-toggle>

              <mat-slide-toggle *ngIf="isPatientPalEmployee && productData.value.codes?.length === 1" formControlName="updateSystemServiceCodes">
                Update system rules for this CPT Code
                <span class="text-warn">(ADMIN)</span>
              </mat-slide-toggle>
            </ng-container>
          </ng-container>

          <mat-form-field appearance="fill" fxFlex>
            <mat-label>Categories</mat-label>
            <mat-select formControlName="categories" multiple (selectionChange)="onCategoryChanged($event)">
              <mat-option
                *ngFor="let productCategory of productCategories$ | async"
                [value]="productCategory.id"
              >
                <span
                  class="colorIcon"
                  [ngStyle]="{ background: productCategory.color }"
                ></span>
                <span>{{ productCategory.label }}</span>
              </mat-option>
            </mat-select>
            <mat-hint
              >Optional. Helps organize procedures into groups.</mat-hint
            >
          </mat-form-field>

          <mat-form-field appearance="fill" fxFlex>
            <mat-label>Primary Category</mat-label>
            <mat-select formControlName="primaryCategory">
              <mat-option
                *ngFor="let productCategory of selectedProductCategories$ | async"
                [value]="productCategory.id"
              >
                <span
                  class="colorIcon"
                  [ngStyle]="{ background: productCategory.color }"
                ></span>
                <span>{{ productCategory.label }}</span>
              </mat-option>
            </mat-select>
            <mat-hint>Optional. Helps organize offerings.</mat-hint>
          </mat-form-field>

          <mat-form-field
            appearance="fill"
            fxFlex
            *ngIf="
              productData.value.type === 'M' || productData.value.type === 'S'
            "
          >
            <mat-label>Default Duration (minutes)</mat-label>
            <input matInput type="number" formControlName="defaultDuration" />
            <mat-error *ngIf="productData.controls.defaultDuration.invalid"
              >{{ getErrorMessage('defaultDuration') }}
            </mat-error>
            <mat-hint
              >Avg duration for this service. Helps when scheduling
              appointments.</mat-hint
            >
          </mat-form-field>

          <mat-checkbox color="primary" (change)="toggleAdvancedSettings()"
            >Show Advanced Settings</mat-checkbox
          >

          <mat-form-field appearance="fill" fxFlex *ngIf="showAdvancedSettings">
            <mat-label>External ID</mat-label>
            <input matInput formControlName="eid" />
            <mat-error *ngIf="productData.controls['eid'].invalid">{{
              getErrorMessage('eid')
            }}</mat-error>
            <mat-hint>Procedure ID used by your external EMR</mat-hint>
          </mat-form-field>

          <mat-error *ngFor="let err of errors">{{ err?.message }}</mat-error>
        </mat-dialog-content>
      </div>

      <div
        class="product-prices"
        fxLayout="column"
        fxLayoutAlign="space-between center"
        fxFlex="50"
      >
        <mmx-product-prices
          #productPrices
          [product]="updatedProduct$ | async"
          (openFeeSchedule)="goToFeeSchedule($event)"
        ></mmx-product-prices>
      </div>
    </div>
    <mat-dialog-actions fxLayout="row" fxLayoutAlign="space-between center">
      <button mat-stroked-button type="button" mat-dialog-close>Cancel</button>
      <!-- The mat-dialog-close directive optionally accepts a value as a result for the dialog. -->
      <button
        mat-flat-button
        color="primary"
        type="submit"
        [disabled]="productData.invalid"
      >
        Save product
      </button>
    </mat-dialog-actions>
  </div>
</form>
